import { supabase } from './supabaseClient.js';

/**
 * Capa de acceso a datos del catálogo. Reconstruye exactamente la misma
 * forma de objeto que antes venía hardcodeada en `const DATA` dentro del
 * HTML: { meta: {...}, skus: { [codigo]: { codigo, descripcion, lineas: { A, B, C } } } }
 * para que el código de renderizado/simuladores existente (src/legacy/appLogic.js)
 * no requiera cambios.
 */

export async function fetchCatalogMeta() {
  const { data, error } = await supabase
    .from('catalogo_meta')
    .select('total_skus, regla_interpretacion, fuente')
    .eq('id', true)
    .maybeSingle();
  if (error) throw error;
  return data || { total_skus: 0, regla_interpretacion: '', fuente: [] };
}

export async function fetchCatalog() {
  const [{ data: skuRows, error: skuErr }, { data: lineaRows, error: lineaErr }, meta] = await Promise.all([
    supabase.from('skus').select('codigo, descripcion, activo').eq('activo', true),
    supabase.from('sku_lineas').select(
      'sku_codigo, linea, produce, excepcion_config, identificacion, amasado, velocidad, derivados, config_manual, empaque_mixto'
    ),
    fetchCatalogMeta(),
  ]);
  if (skuErr) throw skuErr;
  if (lineaErr) throw lineaErr;

  const skus = {};
  for (const row of skuRows) {
    skus[row.codigo] = { codigo: row.codigo, descripcion: row.descripcion, lineas: {} };
  }
  for (const row of lineaRows) {
    if (!skus[row.sku_codigo]) continue; // referencia inactiva/eliminada: se omite del catálogo visible
    skus[row.sku_codigo].lineas[row.linea] = {
      produce: row.produce,
      excepcion_config: row.excepcion_config,
      identificacion: row.identificacion || {},
      amasado: row.amasado || {},
      velocidad: row.velocidad || {},
      derivados: row.derivados || {},
      ...(row.config_manual ? { config_manual: row.config_manual } : {}),
      ...(row.empaque_mixto ? { empaque_mixto: row.empaque_mixto } : {}),
    };
  }

  return {
    meta: { total_skus: meta.total_skus || Object.keys(skus).length, ...meta },
    skus,
  };
}

// --- Administración del catálogo (requiere rol 'admin'; RLS lo hace cumplir igual) ---

export async function listAllSkusForAdmin() {
  const { data, error } = await supabase
    .from('skus')
    .select('codigo, descripcion, activo, updated_at')
    .order('codigo');
  if (error) throw error;
  return data;
}

export async function getSkuWithLineas(codigo) {
  const { data: sku, error: skuErr } = await supabase
    .from('skus').select('codigo, descripcion, activo').eq('codigo', codigo).single();
  if (skuErr) throw skuErr;
  const { data: lineas, error: lineaErr } = await supabase
    .from('sku_lineas').select('*').eq('sku_codigo', codigo);
  if (lineaErr) throw lineaErr;
  return { ...sku, lineas };
}

export async function upsertSku({ codigo, descripcion, activo = true }) {
  const { error } = await supabase.from('skus').upsert({ codigo, descripcion, activo });
  if (error) throw error;
}

export async function setSkuActivo(codigo, activo) {
  const { error } = await supabase.from('skus').update({ activo }).eq('codigo', codigo);
  if (error) throw error;
}

export async function upsertSkuLinea({ sku_codigo, linea, produce, excepcion_config, identificacion, amasado, velocidad, derivados, config_manual, empaque_mixto }) {
  const { error } = await supabase.from('sku_lineas').upsert(
    { sku_codigo, linea, produce, excepcion_config, identificacion, amasado, velocidad, derivados, config_manual, empaque_mixto },
    { onConflict: 'sku_codigo,linea' }
  );
  if (error) throw error;
}
